import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MapPin, ShieldCheck, Star, Trash2, X } from 'lucide-react';
import {
  DAILY_LIFE_COPY,
  EMPTY_STATES,
  PICKUP_LOCATION_COPY,
  PICKUP_RELATIONS,
  PICKUP_REQUEST_STATUS_LABELS,
  PICKUP_TRIP_UPDATE_INTERVAL_MS,
  ROLES,
  formatDistance,
  type PickupRequestStatus,
  type PickupTripKind,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  SectionTitle,
  Spinner,
  Switch,
  Textarea,
} from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { readLocationOnce, watchLocation, type GeoPoint } from '@/lib/geolocation';
import { formatDate, formatPhone, formatPhoneInput, formatRelative, todayISO } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ChildSummary, PickupChangeRequest, PickupConsent, PickupLocationState, PickupTrip } from '@/types';

/** 귀가동의서 — 명세서 §5.10 */
export function PickupsPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;

  const childrenQuery = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });
  const children = childrenQuery.data;

  const [selectedChild, setSelectedChild] = useState<string>('');
  const childId = selectedChild || children?.[0]?.id || '';

  return (
    <Screen>
      <AppBar title="귀가동의서" back />

      <div className="flex flex-col gap-4 p-4">
        <p className="flex items-center gap-1.5 rounded-sm bg-info-soft px-3 py-2 text-sm">
          <ShieldCheck size={15} /> {DAILY_LIFE_COPY.pickup.safetyNotice}
        </p>

        {(children?.length ?? 0) > 1 && (
          <div className="flex flex-wrap gap-2">
            {children?.map((child) => (
              <Chip
                key={child.id}
                tone={child.id === childId ? 'primary' : 'neutral'}
                selected={child.id === childId}
                onClick={() => setSelectedChild(child.id)}
              >
                {child.name}
              </Chip>
            ))}
          </div>
        )}

        {childrenQuery.isPending && <Spinner />}
        {childrenQuery.isError && (
          <ErrorNote message="불러오지 못했어요." onRetry={() => void childrenQuery.refetch()} />
        )}

        {/* 원아가 없으면 아래 섹션이 통째로 사라져 화면이 비어 보인다 — 이유를 말해 준다 */}
        {childrenQuery.isSuccess && !childId && (
          <EmptyState title={EMPTY_STATES.children.title} body={EMPTY_STATES.children.body} />
        )}

        {childId && isParent && <LocationShareSection childId={childId} />}
        {childId && <ConsentSection childId={childId} canEdit={isParent} />}
        {childId && <ChangeSection childId={childId} isParent={isParent} />}
      </div>
    </Screen>
  );
}

function ConsentSection({ childId, canEdit }: { childId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['pickups', 'consents', childId],
    queryFn: () => apiData<PickupConsent[]>(`/pickups/consents?childId=${childId}`),
    placeholderData: keepPreviousData, // 아이 전환 시 목록 유지
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/pickups/consents/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['pickups', 'consents', childId] }),
  });

  const consents = data ?? [];

  return (
    <section>
      <SectionTitle
        action={
          canEdit && (
            <Button size="sm" variant="ghost" onClick={() => setAdding((v) => !v)}>
              {adding ? '닫기' : '추가'}
            </Button>
          )
        }
      >
        정기 하원 담당자
      </SectionTitle>

      {adding && <ConsentForm childId={childId} onClose={() => setAdding(false)} />}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && consents.length === 0 && !adding && (
        <EmptyState
          title={EMPTY_STATES.pickup.title}
          body={EMPTY_STATES.pickup.body}
          action={canEdit && <Button onClick={() => setAdding(true)}>{EMPTY_STATES.pickup.action}</Button>}
        />
      )}

      <ul className="flex flex-col gap-2">
        {consents.map((consent) => (
          <Card key={consent.id} as="li" className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 font-semibold">
                {consent.name}
                {consent.isDefault && (
                  <Chip tone="primary">
                    <Star size={12} /> {DAILY_LIFE_COPY.pickup.defaultBadge}
                  </Chip>
                )}
              </p>
              <p className="text-sm text-ink-sub">
                {consent.relation} · {formatPhone(consent.phone)}
              </p>
            </div>
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`${consent.name} 삭제`}
                onClick={() => remove.mutate(consent.id)}
              >
                <Trash2 size={16} />
              </Button>
            )}
          </Card>
        ))}
      </ul>
    </section>
  );
}

function ConsentForm({ childId, onClose }: { childId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [relation, setRelation] = useState<string>(PICKUP_RELATIONS[0]);
  const [phone, setPhone] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      api('/pickups/consents', {
        method: 'POST',
        body: { childId, name, relation, phone, isDefault },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pickups', 'consents', childId] });
      onClose();
    },
  });

  return (
    <Card className="mb-2">
      <Field label="이름">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 김할머니" />
      </Field>

      <FieldGroup label="관계">
        <div className="flex flex-wrap gap-2">
          {PICKUP_RELATIONS.map((option) => (
            <Chip
              key={option}
              tone={option === relation ? 'primary' : 'neutral'}
              selected={option === relation}
              onClick={() => setRelation(option)}
            >
              {option}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <Field label="연락처">
        <Input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
          placeholder="010-0000-0000"
        />
      </Field>

      <label className="mb-3 mt-1 flex items-center gap-2 text-base">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          className="h-5 w-5 accent-[color:var(--color-primary)]"
        />
        평소 데리러 오시는 분이에요
      </label>

      {submit.isError && (
        <p className="mb-2 text-sm text-notice">{(submit.error as Error).message}</p>
      )}

      <div className="flex gap-2">
        <Button variant="outline" block onClick={onClose}>
          취소
        </Button>
        <Button
          block
          disabled={!name || !phone}
          loading={submit.isPending}
          onClick={() => submit.mutate()}
        >
          등록
        </Button>
      </div>
    </Card>
  );
}

function ChangeSection({ childId, isParent }: { childId: string; isParent: boolean }) {
  const queryClient = useQueryClient();
  const [requesting, setRequesting] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['pickups', 'changes', childId],
    queryFn: () => apiData<PickupChangeRequest[]>(`/pickups/changes?childId=${childId}`),
    placeholderData: keepPreviousData,
  });

  const confirm = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PickupRequestStatus }) =>
      api(`/pickups/changes/${id}/confirm`, { method: 'POST', body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pickups', 'changes', childId] }),
  });

  const changes = data ?? [];

  return (
    <section>
      <SectionTitle
        action={
          isParent && (
            <Button size="sm" variant="ghost" onClick={() => setRequesting((v) => !v)}>
              {requesting ? '닫기' : '변경 요청'}
            </Button>
          )
        }
      >
        당일 변경 요청
      </SectionTitle>

      {requesting && <ChangeForm childId={childId} onClose={() => setRequesting(false)} />}

      {isPending && <Spinner />}
      {!isPending && changes.length === 0 && !requesting && (
        <p className="px-1 py-3 text-base text-ink-sub">아직 변경 요청이 없어요.</p>
      )}

      <ul className="flex flex-col gap-2">
        {changes.map((change) => (
          <Card key={change.id} as="li">
            <div className="mb-1 flex items-center gap-1.5">
              <Chip tone={change.status === 'CONFIRMED' ? 'primary' : 'secondary'}>
                {PICKUP_REQUEST_STATUS_LABELS[change.status]}
              </Chip>
              <span className="ml-auto text-sm text-ink-sub">{formatDate(change.date)}</span>
            </div>
            <p className="font-semibold">
              {change.pickupName} <span className="font-normal text-ink-sub">({change.relation})</span>
            </p>
            <p className="text-sm text-ink-sub">{formatPhone(change.phone)}</p>
            {change.reason && <p className="mt-1 text-base">{change.reason}</p>}

            {!isParent && change.status === 'REQUESTED' && (
              <div className="mt-3 flex gap-2">
                <Button
                  block
                  icon={<Check size={16} />}
                  loading={confirm.isPending}
                  onClick={() => confirm.mutate({ id: change.id, status: 'CONFIRMED' })}
                >
                  확인
                </Button>
                <Button
                  variant="outline"
                  block
                  icon={<X size={16} />}
                  onClick={() => confirm.mutate({ id: change.id, status: 'REJECTED' })}
                >
                  반려
                </Button>
              </div>
            )}
          </Card>
        ))}
      </ul>
    </section>
  );
}

function ChangeForm({ childId, onClose }: { childId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayISO());
  const [pickupName, setPickupName] = useState('');
  const [relation, setRelation] = useState<string>(PICKUP_RELATIONS[0]);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      api('/pickups/changes', {
        method: 'POST',
        body: { childId, date, pickupName, relation, phone, reason: reason || undefined },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pickups', 'changes', childId] });
      onClose();
    },
  });

  return (
    <Card className="mb-2">
      <Field label="날짜">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>

      <Field label="데리러 오시는 분">
        <Input value={pickupName} onChange={(e) => setPickupName(e.target.value)} />
      </Field>

      <FieldGroup label="관계">
        <div className="flex flex-wrap gap-2">
          {PICKUP_RELATIONS.map((option) => (
            <Chip
              key={option}
              tone={option === relation ? 'primary' : 'neutral'}
              selected={option === relation}
              onClick={() => setRelation(option)}
            >
              {option}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <Field label="연락처">
        <Input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
          placeholder="010-0000-0000"
        />
      </Field>

      <Field label="사유">
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>

      {submit.isError && (
        <p className="mb-2 text-sm text-notice">{(submit.error as Error).message}</p>
      )}

      <div className="flex gap-2">
        <Button variant="outline" block onClick={onClose}>
          취소
        </Button>
        <Button
          block
          disabled={!pickupName || !phone}
          loading={submit.isPending}
          onClick={() => submit.mutate()}
        >
          요청
        </Button>
      </div>
    </Card>
  );
}

/**
 * 등하원 위치 공유 (§5.10 v1.22) — 학부모 전용.
 * 동의 스위치를 켠 뒤 "출발했어요"를 누른 동안만 대략적 위치(약 100m)를 담임·원장에게 보여 준다.
 * 화면이 열려 있는 동안 저정밀 위치를 읽어 20초 간격으로 보내고, 도착·끄기·교사 등하원 체크로 끝난다.
 */
function LocationShareSection({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const lastSentAt = useRef(0);

  const state = useQuery({
    queryKey: ['pickups', 'location', childId],
    queryFn: () => apiData<PickupLocationState>(`/pickups/location/me?childId=${childId}`),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pickups', 'location', childId] });
  const fail = (caught: unknown, fallback: string) =>
    setError(caught instanceof ApiError || caught instanceof Error ? caught.message : fallback);

  const consent = useMutation({
    mutationFn: (next: boolean) =>
      api('/pickups/location/consent', { method: 'POST', body: { childId, consent: next } }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (caught) => fail(caught, '동의 상태를 바꾸지 못했어요.'),
  });

  const start = useMutation({
    mutationFn: async (kind: PickupTripKind) => {
      const point = await readLocationOnce({ highAccuracy: false });
      return apiData<PickupTrip>('/pickups/location/trips', {
        method: 'POST',
        body: { childId, kind, lat: point.lat, lng: point.lng },
      });
    },
    onSuccess: () => {
      setError(null);
      lastSentAt.current = Date.now();
      void invalidate();
    },
    onError: (caught) => fail(caught, PICKUP_LOCATION_COPY.permissionDenied),
  });

  const end = useMutation({
    mutationFn: (id: string) => api(`/pickups/location/trips/${id}/end`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (caught) => fail(caught, '공유를 끝내지 못했어요.'),
  });

  const activeTrip = state.data?.activeTrip ?? null;
  const activeId = activeTrip?.id ?? null;

  // 공유 중인 동안만 위치를 감시한다 — 화면을 벗어나면 감시도 멈춘다(마지막 위치는 서버에 남는다)
  useEffect(() => {
    if (!activeId) return;
    let stop: (() => void) | null = null;
    let cancelled = false;
    const send = (point: GeoPoint) => {
      if (Date.now() - lastSentAt.current < PICKUP_TRIP_UPDATE_INTERVAL_MS) return;
      lastSentAt.current = Date.now();
      void api(`/pickups/location/trips/${activeId}`, { method: 'PATCH', body: point })
        .then(() => invalidate())
        .catch(() => {});
    };
    void watchLocation(send, (message) => setError(message), { highAccuracy: false }).then((cleanup) => {
      if (cancelled) cleanup();
      else stop = cleanup;
    });
    return () => {
      cancelled = true;
      stop?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const consented = Boolean(state.data?.consentedAt);

  return (
    <section>
      <SectionTitle>{PICKUP_LOCATION_COPY.title}</SectionTitle>
      <Card className="flex flex-col gap-3">
        <p className="text-sm text-ink-sub">{PICKUP_LOCATION_COPY.intro}</p>

        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium">{PICKUP_LOCATION_COPY.consentLabel}</p>
            <p className="text-sm text-ink-sub">{PICKUP_LOCATION_COPY.consentHint}</p>
          </div>
          <Switch
            checked={consented}
            label={PICKUP_LOCATION_COPY.consentLabel}
            onChange={(next) => consent.mutate(next)}
          />
        </div>

        {consented && !activeTrip && (
          <div className="flex gap-2">
            <Button
              block
              icon={<MapPin size={16} />}
              loading={start.isPending && start.variables === 'DROP_OFF'}
              onClick={() => start.mutate('DROP_OFF')}
            >
              {PICKUP_LOCATION_COPY.startDropOff}
            </Button>
            <Button
              block
              variant="secondary"
              icon={<MapPin size={16} />}
              loading={start.isPending && start.variables === 'PICK_UP'}
              onClick={() => start.mutate('PICK_UP')}
            >
              {PICKUP_LOCATION_COPY.startPickUp}
            </Button>
          </div>
        )}

        {activeTrip && (
          <div className="rounded-sm bg-primary-soft/70 p-3">
            <p className="flex items-center gap-1.5 font-semibold text-primary-strong">
              <MapPin size={15} /> {PICKUP_LOCATION_COPY.sharing(activeTrip.kindLabel)}
            </p>
            <p className="mt-1 text-sm text-ink-sub">
              {PICKUP_LOCATION_COPY.lastSeen(formatRelative(activeTrip.updatedAt))}
              {activeTrip.distanceM !== null &&
                ` · ${PICKUP_LOCATION_COPY.distanceToFacility(formatDistance(activeTrip.distanceM))}`}
            </p>
            <p className="mt-1 text-sm text-ink-sub">{PICKUP_LOCATION_COPY.keepOpenHint}</p>
            <Button
              variant="outline"
              block
              className="mt-3"
              loading={end.isPending}
              onClick={() => end.mutate(activeTrip.id)}
            >
              {PICKUP_LOCATION_COPY.stop}
            </Button>
          </div>
        )}

        {error && <p className="text-sm text-notice">{error}</p>}
      </Card>
    </section>
  );
}
